import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form, Input, Space, Typography, Tag } from "antd";
import {
  ApartmentOutlined,
  CheckCircleFilled,
  LockOutlined,
  LoginOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
// Keycloak disabled for now.
// import {
//   consumePostLoginRedirect,
//   getCurrentSession,
// } from "../services/keycloakSession";

// Keycloak disabled for now.
// const CALLBACK_LOCK_KEY = "keycloak.login.callback_lock";

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login, consumePostLoginRedirect, isAuthenticated, isInitialized } = useAuth();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (isAuthenticated) {
      const redirectTo = consumePostLoginRedirect() || "/dashboard";
      navigate(redirectTo, { replace: true });
    }
  }, [consumePostLoginRedirect, isAuthenticated, isInitialized, navigate]);

  const handleSignIn = async (values) => {
    setLoading(true);
    setError("");

    try {
      await login(values);
      const redirectTo = consumePostLoginRedirect() || "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || "Unable to sign in.");
      setLoading(false);
    }
  };

  return (
    <div className="signin-screen">
      <div className="signin-hero">
        <div className="signin-hero-badge">
          <SafetyOutlined />
          <span>Admin Portal</span>
        </div>

        <Typography.Title level={1} className="signin-hero-title">
          Secure access for the people who run the platform.
        </Typography.Title>

        <Typography.Paragraph type="secondary" className="signin-hero-text">
          Manage customers, plans, features, subscriptions, and access controls from one secure
          control center.
        </Typography.Paragraph>

        <div className="signin-feature-grid">
          <div className="signin-feature-card">
            <LockOutlined />
            <div>
              <Typography.Text strong>Single sign-on</Typography.Text>
              <Typography.Paragraph type="secondary">
                Admin access is protected by the internal JWT login for now.
              </Typography.Paragraph>
            </div>
          </div>
          <div className="signin-feature-card">
            <ThunderboltOutlined />
            <div>
              <Typography.Text strong>Fast workflow</Typography.Text>
              <Typography.Paragraph type="secondary">
                Jump into the dashboard and manage the platform in seconds.
              </Typography.Paragraph>
            </div>
          </div>
          <div className="signin-feature-card">
            <ApartmentOutlined />
            <div>
              <Typography.Text strong>Master admin only</Typography.Text>
              <Typography.Paragraph type="secondary">
                Built for the admin team that oversees the entire ecosystem.
              </Typography.Paragraph>
            </div>
          </div>
        </div>

        <div className="signin-hero-tags">
          <Tag color="blue">Admin auth</Tag>
          <Tag color="geekblue">Role based access</Tag>
          <Tag color="cyan">Secure admin tools</Tag>
        </div>
      </div>

      <Card className="signin-card app-page-card" variant="borderless">
        <div className="signin-card-glow" />
        <Space orientation="vertical" size={18} style={{ width: "100%" }}>
          <div className="signin-badge">
            <SafetyOutlined style={{ fontSize: 28, color: "#4b7cf3" }} />
          </div>

          <div>
            <Typography.Title level={3} style={{ marginBottom: 4 }}>
              Welcome back
            </Typography.Title>
            <Typography.Text type="secondary">
              Sign in to continue to the admin workspace.
            </Typography.Text>
          </div>

          <div className="signin-trust-row">
            <span>
              <CheckCircleFilled />
              Protected session
            </span>
            <span>
              <CheckCircleFilled />
              Role verified
            </span>
          </div>

          {error && (
            <div className="signin-error">
              <Typography.Text type="danger">{error}</Typography.Text>
            </div>
          )}

          <Form layout="vertical" onFinish={handleSignIn} requiredMark={false}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email." },
                { type: "email", message: "Please enter a valid email." },
              ]}
            >
              <Input size="large" placeholder="admin@company.com" autoComplete="email" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Please enter your password." }]}
            >
              <Input.Password
                size="large"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </Form.Item>

            <Button
              type="primary"
              size="large"
              block
              htmlType="submit"
              loading={loading}
              icon={<LoginOutlined />}
              className="signin-action-btn"
            >
              Sign in
            </Button>
          </Form>

          <Typography.Text type="secondary" className="signin-card-footer">
            Use your admin email and password.
          </Typography.Text>
        </Space>
      </Card>
    </div>
  );
};

export default SignIn;
